import { Preferences } from '@capacitor/preferences'

import { api } from './api'

/**
 * Offline-first queue of exercise-delete requests.
 *
 * The picker's trash-icon confirm dialog used to make a synchronous
 * `DELETE /api/patient/exercises/{id}` and refuse to close until the
 * server acknowledged it — fine online, but offline the dialog would
 * sit indefinitely with an error. Per the offline-readiness directive,
 * deletes must be queueable: the user tells the app the exercise is
 * gone, we persist that intent locally, and the actual DELETE goes out
 * the next time we have network.
 *
 * Local state is the source of truth between enqueue and successful
 * flush:
 *   - the on-disk exercises index is rewritten without the deleted row,
 *   - the per-exercise media manifest + training-history cache are
 *     pruned,
 *   - `useExercisesStore.refresh()` filters out anything still in the
 *     delete queue so a server fetch that hasn't seen our DELETE yet
 *     doesn't resurrect the row in the UI.
 *
 * Storage: a single key in `@capacitor/preferences` holding the JSON
 * array of `QueuedDelete`. Mirrors the run-queue's persistence model.
 *
 * Idempotency: we dedupe on `exercise_id` at enqueue time. Server-side
 * the controller scopes by patient relation and 404s when the row is
 * already gone (see `ExerciseController::destroy`) — we treat 404 as
 * a successful drop. Other 4xx are dropped with a `console.warn`
 * because a persistent payload-shape error would otherwise loop
 * forever.
 */

const STORAGE_KEY = 'lingotalk.exercise_deletes.queue'

export interface QueuedDelete {
  exercise_id: number
  attempt_count: number
  last_error?: string
  enqueued_at: string
}

async function readQueue(): Promise<QueuedDelete[]> {
  const { value } = await Preferences.get({ key: STORAGE_KEY })
  if (value === null || value === undefined) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? (parsed as QueuedDelete[]) : []
  } catch {
    return []
  }
}

async function writeQueue(queue: QueuedDelete[]): Promise<void> {
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(queue) })
}

export async function listQueued(): Promise<QueuedDelete[]> {
  return readQueue()
}

/**
 * Read just the exercise ids currently waiting for delete. Used by
 * `useExercisesStore` to filter a server-refresh result — anything we
 * intend to delete must not reappear in the picker.
 */
export async function listQueuedDeleteIds(): Promise<number[]> {
  const queue = await readQueue()
  return queue.map((q) => q.exercise_id)
}

/**
 * Add an exercise id to the delete queue. Idempotent — enqueuing the
 * same id twice is a no-op (the user might have tapped delete on a
 * cached row that the queue already knows about).
 */
export async function enqueueDelete(exerciseId: number): Promise<void> {
  const queue = await readQueue()
  if (queue.some((q) => q.exercise_id === exerciseId)) return

  queue.push({
    exercise_id: exerciseId,
    attempt_count: 0,
    enqueued_at: new Date().toISOString(),
  })
  await writeQueue(queue)
  void flushQueue()
}

/**
 * Walk the queue and DELETE each entry. Same termination policy as the
 * run queue:
 *   - 2xx / 404 → drop (server agrees the row is gone)
 *   - other 4xx → drop with warn (poison payload, retry won't help)
 *   - 5xx / network → keep, bump attempt_count
 */
export async function flushQueue(): Promise<void> {
  const queue = await readQueue()
  if (queue.length === 0) return

  const kept: QueuedDelete[] = []
  for (const entry of queue) {
    try {
      await api.delete(`/patient/exercises/${entry.exercise_id}`)
      // 2xx — drop.
    } catch (e) {
      const status = pickAxiosStatus(e)
      if (status === null) {
        kept.push({
          ...entry,
          attempt_count: entry.attempt_count + 1,
          last_error: stringifyError(e),
        })
      } else if (status >= 500) {
        kept.push({
          ...entry,
          attempt_count: entry.attempt_count + 1,
          last_error: `HTTP ${status}`,
        })
      } else if (status === 404) {
        // Already gone server-side (e.g. deleted on another device, or
        // a previous successful flush whose response we missed). Drop.
      } else {
        console.warn(
          `[delete-queue] dropping un-recoverable delete for exercise ${entry.exercise_id}: HTTP ${status}`,
        )
      }
    }
  }
  await writeQueue(kept)
}

/**
 * Drop a single entry by exercise id. Exposed for tests + the future
 * "discard offline deletes" debug path. Not on the happy path.
 */
export async function dropFromQueue(exerciseId: number): Promise<void> {
  const queue = await readQueue()
  const next = queue.filter((q) => q.exercise_id !== exerciseId)
  if (next.length !== queue.length) {
    await writeQueue(next)
  }
}

function pickAxiosStatus(err: unknown): number | null {
  if (err && typeof err === 'object') {
    const response = (err as { response?: { status?: unknown } }).response
    const status = response?.status
    if (typeof status === 'number') return status
  }
  return null
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}
